import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Modal } from "../../components/modal";
import { PersonForm, PersonFormValues } from "../../components/person-form";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { useGetPerson } from "../../queries/person";
import {
  parseAdditionalFieldToFormValue,
  parseFormValuesToAdditionalFields,
} from "../../services/additional-fields";
import { updatePerson } from "../../services/person";
import { navigateBack } from "../../utils/navigate-back";

export function PersonEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Person ID is required");
  }

  const { data: person, isLoading: isLoadingPerson } = useGetPerson(id);

  const { data: additionalFields } = useAdditionalFields("person");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<PersonFormValues>({
    personalIdentificationNumber: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    additionalFields: additionalFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: "",
      }),
      {},
    ),
  });

  const isLoading = isLoadingPerson || loading;

  useEffect(() => {
    if (person) {
      setFormValues({
        personalIdentificationNumber: person.personalIdentificationNumber,
        firstName: person.firstName ?? "",
        lastName: person.lastName ?? "",
        email: person.email ?? "",
        phone: person.phone ?? "",
        additionalFields: additionalFields.reduce(
          (acc, field) => ({
            ...acc,
            [field.name]: parseAdditionalFieldToFormValue(
              person?.additionalData?.[field.name],
            ),
          }),
          {},
        ),
      });
    }
  }, [person, additionalFields]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/person");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <PersonForm
        hasError={hasError}
        loading={isLoading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await updatePerson(id, {
                personalIdentificationNumber:
                  formValues.personalIdentificationNumber,
                ...(formValues.firstName && {
                  firstName: formValues.firstName,
                }),
                ...(formValues.lastName && { lastName: formValues.lastName }),
                ...(formValues.email && { email: formValues.email }),
                ...(formValues.phone && { phone: formValues.phone }),
                ...(formValues.additionalFields && {
                  additionalData: parseFormValuesToAdditionalFields(
                    formValues.additionalFields,
                    additionalFields,
                  ),
                }),
              });
              Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["person", id],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["persons"],
                }),
              ]);
              closeModal();
            } catch (error: unknown) {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
